import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faEnvelope, faPhone, faMapMarkerAlt, faGlobe, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useProject } from '../../../contexts/ProjectContext';

const ArchivedProjectModal = ({ isOpen, onClose, projectName }) => {
  const { project } = useProject();
  const organization = project?.organization;
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div 
        className="bg-white rounded-lg max-w-md w-full shadow-theme-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-slate-500 flex items-center justify-center">
                <FontAwesomeIcon icon={faArchive} className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Proyecto Archivado</h2>
                <p className="text-sm text-gray-600 mt-0.5">Estado: Completado/Archivado</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              El proyecto <strong className="font-semibold text-gray-900">{projectName || 'este proyecto'}</strong> ha sido <strong className="font-semibold text-slate-700">completado o archivado</strong>. 
              Los lotes ya no están disponibles para venta.
            </p>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-slate-600 text-xs font-bold">📦</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Proyecto Completado</p>
              <p className="text-xs text-gray-600 mt-1">Este proyecto ha finalizado su etapa de venta y ha sido archivado en nuestro sistema.</p>
            </div>
          </div>

          {/* Información de la Organización */}
          {organization ? (
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-3">
                <FontAwesomeIcon icon={faBuilding} className="text-slate-600 text-sm" />
                <h3 className="text-sm font-semibold text-gray-900">Información de Contacto</h3>
              </div>

              {/* Logo y Nombre */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  {organization.logo_url ? (
                    <img
                      src={organization.logo_url}
                      alt={`Logo de ${organization.name}`}
                      className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center border border-gray-200">
                      <FontAwesomeIcon icon={faBuilding} className="text-slate-600 text-lg" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{organization.name}</h4>
                    {organization.description && (
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{organization.description}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Información de Contacto */}
              <div className="space-y-2">
                {organization.address && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-slate-600 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">Dirección</div>
                        <div className="text-xs font-medium text-gray-900">{organization.address}</div>
                      </div>
                    </div>
                  </div>
                )}

                {organization.phone && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faPhone} className="text-green-600 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">Teléfono</div>
                        <a 
                          href={`tel:${organization.phone}`}
                          className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          {organization.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {organization.email && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">Email</div>
                        <a 
                          href={`mailto:${organization.email}`}
                          className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline truncate block"
                        >
                          {organization.email}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {organization.website && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faGlobe} className="text-purple-600 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-600 mb-0.5 uppercase tracking-wide">Sitio Web</div>
                        <a 
                          href={organization.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline truncate block"
                        >
                          {organization.website}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-600">No hay información de contacto disponible</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchivedProjectModal;

